"use client";
import SuccessToast from "../components/toastMessages/SuccessToast";
import React, { createContext, useCallback, useContext, useState } from "react";

type Toast = {
  id: number;
  type: "success" | "info" | "loading" | "error" | undefined;
  msg: string;
};

export type ToastContext = {
  setToast: (
    msg: string,
    type: "success" | "info" | "loading" | "error" | undefined
  ) => void;
};

const toastContext = createContext<ToastContext | undefined>(undefined);

let externalShowToast: ToastContext['setToast'];

export const ToastContextProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [toastArr, setToastArr] = useState<Toast[]>([]);

  const autoRemove = useCallback(
    (id: number) => {
      setTimeout(() => {
        setToastArr((prevData) => prevData.filter((data) => data.id !== id));
      }, 5000);
    },
    [setToastArr]
  );

  const setToast = useCallback(
    (
      msg: string,
      type: "success" | "info" | "loading" | "error" | undefined
    ) => {
      const id = Date.now();
      setToastArr((prevData) => [{ id, msg, type }, ...prevData]);
      autoRemove(id);
    },
    [setToastArr, autoRemove]
  );

  externalShowToast = setToast;

  const capitalizeFirstLetter = (text?: string) => {
    if (!text) return "Info";
    return text.charAt(0).toUpperCase() + text.slice(1);
  };

  return (
    <toastContext.Provider value={{ setToast }}>
      {toastArr.map((toast, index) => (
        <SuccessToast
          key={index}
          toastOpen={true}
          handleToast={() => {}}
          toastTitle={capitalizeFirstLetter(toast.type)}
          toastSubtitle={toast.msg}
          toastType={toast.type}
          margin={index * 70}
        />
      ))}
      {children}
    </toastContext.Provider>
  );
};

export const useToastContext = () => {
  return useContext(toastContext);
};

export const showToastOutsideReact = (msg: string, type?: 'success' | 'error' | 'info') => {
  if (externalShowToast) {
    externalShowToast(msg, type);
  } else {
    console.warn('Toast function not initialized yet');
  }
};